import { useEffect } from "react";

import { writeTenant } from "../logic/tenant-storage.ts";
import { useTenantSetting } from "./use-tenant-setting";

export const THEME_LINK_ID = "tenant-theme-css";

/** be-fan sets this attribute when it resolved the tenant for this request. */
const RESOLVED_ATTRIBUTE = "data-tenant-resolved";

/**
 * Keeps the tenant theme stylesheet pointed at the current tenant.
 *
 * be-fan injects the href when the document request carries a tenant, and
 * marks that link with `data-tenant-resolved`. A marked slug wins over local
 * storage, because it belongs to the request the fan just made. An unmarked
 * href carries no authority, because a cached document can hold a slug from an
 * older template. A request with no tenant signal ships a link with no href,
 * which this hook fills from local storage on every route.
 *
 * The browser never retries a stylesheet, and it often fails before this hook
 * mounts. So the hook retries once, on an error event or on a link that holds
 * no rules after the document loads.
 */
export function useTenantThemeStylesheet() {
  const storedTenant = useTenantSetting();

  useEffect(() => {
    const link = document.getElementById(
      THEME_LINK_ID,
    ) as HTMLLinkElement | null;

    if (!link) {
      return;
    }

    const resolvedTenant = link.hasAttribute(RESOLVED_ATTRIBUTE)
      ? readTenantFromHref(link.getAttribute("href"))
      : null;

    if (resolvedTenant && resolvedTenant !== storedTenant) {
      // Adopt the server's tenant, so the theme and the API calls in `api/`
      // agree on one tenant.
      writeTenant(resolvedTenant);
    }

    const tenant = resolvedTenant ?? storedTenant;

    if (!tenant) {
      return;
    }

    const href = buildThemeHref(tenant);
    // The browser already requested a matching href. Anything else is ours to
    // set, and its error event is still ahead of us.
    const isBrowserRequest =
      readTenantFromHref(link.getAttribute("href")) === tenant;

    if (!isBrowserRequest) {
      link.setAttribute("href", href);
    }

    let hasRetried = false;

    const retry = () => {
      if (hasRetried) {
        return;
      }

      hasRetried = true;

      // The query parameter defeats the browser cache. A repeat of the same
      // href starts no request.
      link.setAttribute("href", `${href}&retry=1`);
    };

    const retryIfUnloaded = () => {
      if (!isStylesheetLoaded(link)) {
        retry();
      }
    };

    link.addEventListener("error", retry);

    // A stylesheet that the document requested fails before this hook mounts,
    // so no error event survives. Check the result instead, once the document
    // completes. A pending stylesheet holds the document open, so a complete
    // document means the request settled.
    if (isBrowserRequest) {
      if (document.readyState === "complete") {
        retryIfUnloaded();
      } else {
        window.addEventListener("load", retryIfUnloaded);
      }
    }

    return () => {
      link.removeEventListener("error", retry);
      window.removeEventListener("load", retryIfUnloaded);
    };
  }, [storedTenant]);
}

/** A failed sheet can be null, or opaque with a throwing `cssRules`. */
function isStylesheetLoaded(link: HTMLLinkElement): boolean {
  try {
    return (link.sheet?.cssRules.length ?? 0) > 0;
  } catch {
    return false;
  }
}

function buildThemeHref(tenant: string): string {
  return `/api/theme.css?tenant=${encodeURIComponent(tenant)}`;
}

function readTenantFromHref(href: string | null): string | null {
  if (!href) {
    return null;
  }

  return new URL(href, window.location.origin).searchParams.get("tenant");
}
